import { useEffect, useRef, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { GiftRetentionSelector, type DeliveryMode } from "./GiftRetentionSelector";
import { appState } from "../../../state";
import { getDirectory } from "../../../mentions";
import { sendGiftApi, sendGiftFromExistingApi } from "../../../giftsApi";
import { showToast } from "../../../toast";
import { isIOS } from "../../../utils";
import { mediaUrl } from "../../../mediaAuth";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // matches chat attachments
const MAX_VIDEO_SIZE = 500 * 1024 * 1024;
const MIN_LOCK_LEAD_MINUTES = 5; // headroom over the server's 1-minute floor

// UnlockDateTimePicker wants separate yyyy-mm-dd/HH:mm strings in local
// wall-clock time - formatted by hand since there's no existing helper for
// this in utils.ts.
function minUnlockParts(): { date: string; time: string } {
  const d = new Date(Date.now() + MIN_LOCK_LEAD_MINUTES * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

// Kept deliberately minimal (Snapchat/WhatsApp-status inspired): who to
// send to only shows as a picker when there's an actual choice to make -
// with just one other person on the account (the common case for this
// app), it's skipped entirely. Attach + "view once" toggle sit on one
// compact row, same paperclip-icon-button pattern as chat's composer
// (chatAttachBtn); no separate "Keep" control - toggling view-once off
// just sends a normal (non-ephemeral) gift, same as before, just without
// a second labeled button for it. No album picker here - which album (if
// any) a non-once gift lands in is the *recipient's* call, made after
// they've seen it (GiftsScreen.tsx's "Save to Album" action), not the
// sender's.
const MAX_MESSAGE_LENGTH = 300;
export interface GiftPresetItem {
  filename: string;
  type: "image" | "video";
}

// presetItem: opened from the Lightbox's "Send as Gift" action - skips the
// file picker entirely and gifts that exact gallery item instead of
// requiring a fresh (duplicate) upload. Undefined/null for the normal
// entry point (the Gifts tab's own "Send a Gift" button), which keeps
// requiring a file exactly as before.
export function SendGiftDialog({
  open,
  onClose,
  presetItem,
}: {
  open: boolean;
  onClose: () => void;
  presetItem?: GiftPresetItem | null;
}) {
  const recipients = getDirectory().filter((u) => u.email !== appState.currentUserEmail);
  const needsRecipientPicker = recipients.length > 1;
  const soleRecipient = recipients.length === 1 ? recipients[0] : null;

  // Not initialized from soleRecipient in a mount/open effect: loadDirectory()
  // is one of several unawaited parallel calls in AuthGate's
  // startProtectedFeatures(), so the directory can still be empty at the
  // instant this dialog first opens. Deriving the fallback at render time
  // instead means it's always correct once the directory arrives, with no
  // dependency on effect timing - same fix already proven in
  // WatchTogetherForm.tsx/SendGiftScreen.tsx.
  const [recipientEmailChoice, setRecipientEmail] = useState("");
  const recipientEmail = recipientEmailChoice || soleRecipient?.email || "";
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>("once");
  const [unlockDate, setUnlockDate] = useState("");
  const [unlockTime, setUnlockTime] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revokes the previous object URL whenever the picked file changes (or
  // the dialog unmounts) - otherwise each new selection leaks the last
  // preview's blob URL for the life of the page.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function reset(): void {
    // Clears the explicit choice, not the soleRecipient fallback itself -
    // recipientEmail's own derivation above already falls back to
    // soleRecipient?.email whenever there's no explicit choice.
    setRecipientEmail("");
    setDeliveryMode("once");
    setUnlockDate("");
    setUnlockTime("");
    setMessage("");
    setFile(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleClose(): void {
    if (sending) return;
    reset();
    onClose();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0] || null;
    if (!picked) {
      setFile(null);
      return;
    }
    const isVideo = /\.(mp4|mov|webm|avi)$/i.test(picked.name);
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (picked.size > maxSize) {
      setError(`File too large (max ${isVideo ? "500MB for videos" : "10MB for photos"}).`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setError("");
    setFile(picked);
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError("");

    if (!recipientEmail) {
      setError("Choose who this gift is for.");
      return;
    }
    if (!presetItem && !file) {
      setError("Attach a photo or video to send.");
      return;
    }
    if (deliveryMode === "unlock_later" && (!unlockDate || !unlockTime)) {
      setError("Choose when this gift should unlock.");
      return;
    }

    // "Unlock later" gifts always send as "keep" under the hood - the
    // server derives the third delivery mode from mode:"keep" + a future
    // unlockAt, it isn't a stored mode value itself (see server.js's
    // giftDeliveryMode()).
    const mode = deliveryMode === "once" ? "once" : "keep";
    const unlockAtIso = deliveryMode === "unlock_later" ? new Date(`${unlockDate}T${unlockTime}`).toISOString() : null;

    const trimmedMessage = message.trim() || undefined;

    setSending(true);
    const result = presetItem
      ? await sendGiftFromExistingApi({ filename: presetItem.filename, recipientEmail, mode, unlockAt: unlockAtIso, message: trimmedMessage })
      : await sendGiftApi({ recipientEmail, mode, file: file!, unlockAt: unlockAtIso, message: trimmedMessage });
    setSending(false);

    if (!result.ok) {
      setError(result.error || "Failed to send gift. Try again.");
      return;
    }

    showToast(`Gift sent to ${result.gift.recipientName} ✓`, "success");

    reset();
    onClose();
  }

  const isVideoFile = presetItem ? presetItem.type === "video" : file ? /\.(mp4|mov|webm|avi)$/i.test(file.name) : false;
  // The real gallery asset for a preset item (not a local blob - nothing
  // was picked from disk), the just-picked file's local preview otherwise.
  const displaySrc = presetItem ? mediaUrl(`/assets/media/${presetItem.filename}`) : previewUrl;
  // See GiftViewerDialog.tsx - iOS Safari can fail to apply clip-path:
  // url(#id) (objectBoundingBox SVG clipPath) to img/video elements, so the
  // preview falls back to a plain rounded frame there instead of risking an
  // invisible/broken preview.
  const heartClip = isIOS() ? undefined : "url(#giftHeartClip)";

  return (
    <AppDialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      {/* Zero-size, never visually rendered itself - just declares the
          heart clip path other elements reference by id. objectBoundingBox
          units (0-1) so it scales to whatever box size uses it, unlike a
          plain clip-path: path() whose coordinates are fixed CSS pixels. */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <clipPath id="giftHeartClip" clipPathUnits="objectBoundingBox">
            <path d="M 0.5 0.85 C 0.2 0.6, 0 0.4, 0 0.25 C 0 0.1, 0.15 0, 0.28 0 C 0.38 0, 0.46 0.05, 0.5 0.15 C 0.54 0.05, 0.62 0, 0.72 0 C 0.85 0, 1 0.1, 1 0.25 C 1 0.4, 0.8 0.6, 0.5 0.85 Z" />
          </clipPath>
        </defs>
      </svg>
      <AppDialogHeader icon="bi-gift-fill" title="Send a Gift" onBack={handleClose} disabled={sending} />
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: -0.5 }}>
          Send something just for them.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          {needsRecipientPicker && (
            <TextField
              select
              label="Send to"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              fullWidth
              size="small"
              disabled={sending}
            >
              {recipients.map((u) => (
                <MenuItem key={u.email} value={u.email}>
                  {u.fullName || u.email}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!recipients.length && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              No one else to send gifts to yet.
            </Alert>
          )}

          {!presetItem && <input ref={fileInputRef} type="file" accept="image/*,video/*" hidden onChange={handleFileChange} disabled={sending} />}

          {displaySrc ? (
            <Box className="send-gift-preview">
              {isVideoFile ? (
                <video src={displaySrc} muted playsInline className="send-gift-preview-media" style={{ clipPath: heartClip, borderRadius: heartClip ? undefined : 20 }} />
              ) : (
                <img
                  src={displaySrc}
                  alt="Gift preview"
                  className="send-gift-preview-media"
                  style={{ clipPath: heartClip, borderRadius: heartClip ? undefined : 20 }}
                />
              )}
              {!presetItem && (
                <button type="button" className="send-gift-preview-change" onClick={() => fileInputRef.current?.click()} disabled={sending}>
                  Change
                </button>
              )}
            </Box>
          ) : (
            !presetItem && (
              <button type="button" className="send-gift-picker" onClick={() => fileInputRef.current?.click()} disabled={sending}>
                <i className="bi bi-image" aria-hidden="true" />
                <span>Choose a photo or video</span>
              </button>
            )
          )}

          <GiftRetentionSelector
            deliveryMode={deliveryMode}
            dateValue={unlockDate}
            timeValue={unlockTime}
            minDate={minUnlockParts().date}
            dateTimeError={null}
            sending={sending}
            onSelectMode={setDeliveryMode}
            onDateChange={setUnlockDate}
            onTimeChange={setUnlockTime}
          />

          <TextField
            label="Add a message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            disabled={sending}
            helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
          />

          {error && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          <Button type="submit" variant="contained" color="primary" disabled={sending || !recipients.length} aria-label="Send gift">
            <i className="bi bi-gift-fill me-2" aria-hidden="true" style={{ marginRight: 8 }} />
            {sending ? "Sending…" : "Send Gift"}
          </Button>
        </Box>
      </DialogContent>
    </AppDialog>
  );
}
